import { requireAnySystemPermission } from '../../../../backend/features/permissions/shared';
export function buildOrganizationTree(context) {
    return buildOrganizationTreeChildren(context.storage('organization').list({ orderBy: [{ column: 'name' }] }).rows);
}
export function buildOrganizationTreeChildren(rows) {
    var _a, _b, _c;
    const items = [];
    const idOrgMap = {};
    const networkIdMap = {};
    const orgWithReportingIdSet = new Set();
    for (const row of rows) {
        idOrgMap[row.id] = {
            id: row.id,
            name: row.columns.name,
            visible: row.columns.visible,
            disabled: !row.columns.visible,
            children: [],
            path: (_a = row.columns.path) !== null && _a !== void 0 ? _a : '',
            reportingId: row.columns.reportingId,
            type: row.columns.type,
        };
        (_b = row.columns.parentIds) === null || _b === void 0 ? void 0 : _b.forEach((alternativeParentId) => {
            if (!networkIdMap[alternativeParentId]) {
                networkIdMap[alternativeParentId] = [];
            }
            networkIdMap[alternativeParentId].push(row.id);
        });
        if (!!((_c = row.columns.reportingId) === null || _c === void 0 ? void 0 : _c.trim())) {
            orgWithReportingIdSet.add(row.id);
        }
    }
    for (const row of rows) {
        if (row.parentId) {
            idOrgMap[row.parentId].children.push(idOrgMap[row.id]);
        }
        else {
            items.push(idOrgMap[row.id]);
        }
    }
    orgWithReportingIdSet.forEach((id) => {
        cascadeOrganizationReportingId(idOrgMap[id]);
    });
    for (const networkParentId in networkIdMap) {
        const networkParentOrg = idOrgMap[networkParentId];
        const networkOrganizationIds = networkIdMap[networkParentId];
        networkOrganizationIds.forEach((orgId) => {
            const alternativeChildOriginal = idOrgMap[orgId];
            const copy = cascadeOrganizationTreeNetwork(alternativeChildOriginal, networkParentOrg.path, networkIdMap, idOrgMap);
            networkParentOrg.children.push(copy);
        });
    }
    return items;
}
export function cascadeOrganizationTreeNetwork(organization, parentOrganizationPath, networkIdMap, idOrgMap) {
    // we must make a copy of the organization first
    const orgCopy = Object.assign(Object.assign({}, organization), { fromNetwork: true, path: `${parentOrganizationPath}/${organization.name}`, children: [] });
    // create a network subtree from direct children
    organization.children.forEach((childOrg) => {
        const childOrgCopy = cascadeOrganizationTreeNetwork(childOrg, orgCopy.path, networkIdMap, idOrgMap);
        orgCopy.children.push(childOrgCopy);
    });
    // create a network subtree from network children as well
    /**
     * after adding organizations network subtrees to our copy (the root of subtree),
     * these subtrees become direct children with `fromNetwork` property
     * `directChildrenIds` set bellow lets us know already visited organizations
     * to prevent duplicate entries in any of subtrees
     *
     * example:
     * having organizations
     *  - A (primary)
     *  - B (primary)
     *  - C (primary)
     *
     * without checking if organizations are present in direct children of subtree root organization
     * we get all copies duplicated because it visits both: direct and alternative children, so we get this
     *
     *  - A (primary)
     *      - B (copy with path A/B)
     *  - B (primary)
     *  - C (primary)
     *      - A (copy with path C/A)
     *          - B (copy with path C/A/B)
     *          - B (BUG - a copy with path A/B!)
     */
    const directChildrenIds = new Set(organization.children.map((childOrg) => childOrg.id));
    if (networkIdMap[organization.id]) {
        for (const altChildId of networkIdMap[organization.id]) {
            if (!directChildrenIds.has(altChildId)) {
                const altChild = idOrgMap[altChildId];
                const altChildCopy = cascadeOrganizationTreeNetwork(altChild, orgCopy.path, networkIdMap, idOrgMap);
                orgCopy.children.push(altChildCopy);
            }
        }
    }
    return orgCopy;
}
export function cascadeOrganizationReportingId(organization) {
    organization.children.forEach((childOrg) => {
        var _a;
        if (!((_a = childOrg.reportingId) === null || _a === void 0 ? void 0 : _a.trim())) {
            childOrg.reportingId = organization.reportingId;
        }
        cascadeOrganizationReportingId(childOrg);
    });
}
export function buildOrganizationTreeForUser(organizations, userState, parents) {
    const userOrganizations = [];
    let nonNetworkOrganizationsCount = 0;
    for (const organization of organizations) {
        const userOrganization = {
            id: organization.id,
            name: organization.name,
            visible: organization.visible,
            fromNetwork: organization.fromNetwork,
            children: [],
            path: organization.path,
            reportingId: organization.reportingId,
            type: organization.type,
        };
        const childResult = buildOrganizationTreeForUser(organization.children, userState, parents ? [...parents, userOrganization] : [userOrganization]);
        userOrganization.children = childResult.organizations;
        if (userOrganization.visible ||
            requireAnySystemPermission(userState, 'view-invisible-organizations') ||
            userOrganization.children.length > 0 ||
            (userState && userState.orgMembership.includes(userOrganization.id)) ||
            requireAnySystemPermission(userState, 'all-organizations-admin')) {
            userOrganizations.push(userOrganization);
            if (!organization.fromNetwork) {
                nonNetworkOrganizationsCount += 1 + childResult.totalUserOrganizations;
            }
        }
    }
    return { organizations: userOrganizations, totalUserOrganizations: nonNetworkOrganizationsCount };
}
export function findOrganization(organizations, id, skipNetwork) {
    for (const organization of organizations) {
        if (organization.id.startsWith(id)) {
            if (skipNetwork && organization.fromNetwork) {
                return undefined;
            }
            return organization;
        }
        const foundChild = findOrganization(organization.children, id, skipNetwork);
        if (foundChild) {
            return foundChild;
        }
    }
    return undefined;
}
export function findOrganizationByPath(organizations, path) {
    for (const organization of organizations) {
        if (organization.path.toLowerCase() === path.toLowerCase()) {
            return organization;
        }
        const foundChild = findOrganizationByPath(organization.children, path);
        if (foundChild) {
            return foundChild;
        }
    }
    return undefined;
}
/*
 * @deprecated organization names are no longer unique, meaning you will receive first organization that matches name. Use findOrganization function
 * */
export function findOrganizationByName(organizations, name) {
    for (const organization of organizations) {
        if (organization.name.toLocaleLowerCase() === name.toLocaleLowerCase()) {
            return organization;
        }
        const foundChild = findOrganizationByName(organization.children, name);
        if (foundChild) {
            return foundChild;
        }
    }
    return undefined;
}
export function getNumberOfOrganizationsWithName(organizations, name) {
    let count = 0;
    function traverseTree(organizations) {
        for (const organization of organizations) {
            if (organization.name.toLocaleLowerCase() === name.toLocaleLowerCase()) {
                count++;
            }
            if (organization.children.length > 0) {
                traverseTree(organization.children);
            }
        }
    }
    traverseTree(organizations);
    return count;
}
export function filterOrganizationsForUser(organizations, includeVisible, includeByMembership, includeByOrgPermission, userState) {
    if (requireAnySystemPermission(userState, 'all-organizations-admin', 'manage-organizations')) {
        return organizations;
    }
    const canViewInvisible = requireAnySystemPermission(userState, 'view-invisible-organizations');
    return filterOrganizationTreeItems(organizations, (organization) => {
        var _a;
        if (includeVisible && (organization.visible || canViewInvisible)) {
            return true;
        }
        else if (includeByMembership && (userState === null || userState === void 0 ? void 0 : userState.orgMembership.includes(organization.id.split('_')[0]))) {
            return true;
        }
        else if (userState && includeByOrgPermission) {
            for (const orgPermission of includeByOrgPermission) {
                if ((_a = userState.orgPermissions[organization.id.split('_')[0]]) === null || _a === void 0 ? void 0 : _a.includes(orgPermission)) {
                    return true;
                }
            }
        }
        return false;
    });
}
export function filterOrganizationTreeItems(items, filter) {
    const filteredItems = [];
    let anyMatched = false;
    for (const item of items) {
        const matched = filter(item);
        anyMatched = anyMatched || matched;
        let children;
        if (item.children) {
            children = filterOrganizationTreeItems(item.children, filter);
            anyMatched = anyMatched || (children === null || children === void 0 ? void 0 : children.length) > 0;
        }
        if (matched) {
            filteredItems.push(Object.assign(Object.assign({}, item), { children: children !== null && children !== void 0 ? children : [] }));
        }
        else if (children && children.length > 0) {
            // Current item has children that meet the filter criteria, we need to include it in the organization tree;
            // however, since this parent item doesn't meet the filter criteria we need to disable it.
            filteredItems.push(Object.assign(Object.assign({}, item), { children: children !== null && children !== void 0 ? children : [], disabled: true }));
        }
    }
    return filteredItems;
}
